import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo, useRef } from 'react';
import { Camera, Video, Square, X, ImageIcon, Loader2 } from 'lucide-react';
import {
  captureSupported, captureScreenshotFrame, startScreenRecording, uploadCapture,
  RECORD_MAX_MS,
  type RecordingHandle,
} from '../../lib/screen-capture';
import { ScreenshotAnnotator } from './ScreenshotAnnotator';
import { FormInput } from './FormInput';
import { FormTextarea } from './FormTextarea';
import { FormSelect } from './FormSelect';
import { FormCombobox } from './FormCombobox';
import { Button } from '../ui/Button';
import { SubmissionProgress } from '../ui/SubmissionProgress';
import { useAuth } from 'react-oidc-context';
import { useAuthUser } from '../../hooks/useAuthUser';
import { isOidcConfigured } from '../../lib/auth';
import {
  itSupportFormSchema,
  type ITSupportFormValues,
  departmentOptions,
  itRequestTypeOptions,
  websiteIssueRequestType,
} from '../../lib/validation';
import { WebsiteIssueList } from '../WebsiteIssueButton';
import allUsers from '../../data/onelogin-users.json';
import excludedEmails from '../../data/excluded-emails.json';

const deptOptions = departmentOptions.map((opt) => ({ value: opt, label: opt }));
const reqTypeOptions = itRequestTypeOptions.map((opt) => ({ value: opt, label: opt }));

const EXCLUDED_EMAILS = new Set(excludedEmails.map((e) => e.toLowerCase()));

export function ITSupportForm() {
  const navigate = useNavigate();
  const authUser = useAuthUser();
  const auth = isOidcConfigured() ? useAuth() : null; // eslint-disable-line react-hooks/rules-of-hooks
  const [submissionStatus, setSubmissionStatus] = useState<
    'idle' | 'submitting' | 'success' | 'error'
  >('idle');
  const [submissionError, setSubmissionError] = useState('');

  const emailOptions = useMemo(
    () =>
      allUsers
        .filter((u) => !EXCLUDED_EMAILS.has(u.email.toLowerCase()))
        .map((u) => ({ value: u.email, label: u.name })),
    [],
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<ITSupportFormValues>({
    resolver: zodResolver(itSupportFormSchema),
    defaultValues: {
      email: authUser.email || '',
      department: '',
      requestType: '',
      subject: '',
      description: '',
    },
  });

  // Auto-fill email from SSO when available
  useEffect(() => {
    if (authUser.isAuthenticated && authUser.email) {
      setValue('email', authUser.email);
    }
  }, [authUser.isAuthenticated, authUser.email, setValue]);

  // "Website Issue" doesn't create a ticket: the rest of the form is replaced
  // by the website picker, which opens the chosen site with the Page Feedback
  // annotation widget enabled — the report goes straight to the web team.
  const isWebsiteIssue = watch('requestType') === websiteIssueRequestType;

  // ── Desktop capture (screenshot + annotate, or ≤60s recording) ─────────
  // Captures upload straight to Vercel Blob; the chip shows an uploading
  // spinner until its URL is back. Only uploaded items go on the ticket.
  type UploadItem = { key: string; name: string; type: string; size: number; url?: string; uploading: boolean; error?: string };
  const [items, setItems] = useState<UploadItem[]>([]);
  const [annotating, setAnnotating] = useState<string | null>(null);
  const [recElapsed, setRecElapsed] = useState<number | null>(null);
  const [captureError, setCaptureError] = useState('');
  const recRef = useRef<RecordingHandle | null>(null);
  const keySeq = useRef(0);
  useEffect(() => () => recRef.current?.stop(), []);

  const uploadingCount = items.filter((i) => i.uploading).length;

  async function ingest(file: File) {
    setCaptureError('');
    const key = `c${++keySeq.current}`;
    setItems((prev) => [...prev, { key, name: file.name, type: file.type, size: file.size, uploading: true }]);
    try {
      const att = await uploadCapture(file, auth?.user?.access_token);
      setItems((prev) => prev.map((i) => (i.key === key ? { ...i, url: att.url, uploading: false } : i)));
    } catch {
      setItems((prev) => prev.map((i) => (i.key === key ? { ...i, uploading: false, error: 'Upload failed — remove and retry' } : i)));
    }
  }

  function dataUrlToFile(dataUrl: string, name: string): File {
    const [head, b64] = dataUrl.split(',');
    const mime = /:(.*?);/.exec(head)?.[1] ?? 'image/jpeg';
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new File([bytes], name, { type: mime });
  }

  async function captureShot() {
    setCaptureError('');
    const frame = await captureScreenshotFrame();
    if (frame) setAnnotating(frame);
  }

  async function toggleRecording() {
    if (recRef.current) {
      recRef.current.stop();
      return;
    }
    setCaptureError('');
    const handle = await startScreenRecording({
      onTick: (ms) => setRecElapsed(ms),
      onDone: (file, error) => {
        recRef.current = null;
        setRecElapsed(null);
        if (!file) {
          if (error) setCaptureError(error);
          return;
        }
        void ingest(file);
      },
    });
    if (handle) {
      recRef.current = handle;
      setRecElapsed(0);
    }
  }

  const onSubmit = async (data: ITSupportFormValues) => {
    setSubmissionStatus('submitting');
    setSubmissionError('');

    try {
      const accessToken = auth?.user?.access_token;
      const response = await fetch('/api/it-support', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          formType: 'it-support',
          submittedAt: new Date().toISOString(),
          data,
          // Desktop captures already uploaded to Blob — send just the URLs.
          ...(() => {
            const done = items.filter((i) => i.url);
            return done.length > 0
              ? { attachments: done.map(({ name, type, size, url }) => ({ name, type, size, url })) }
              : {};
          })(),
        }),
      });

      if (!response.ok) {
        throw new Error(`Something went wrong (error ${response.status}). Please try again.`);
      }

      setSubmissionStatus('success');
      setTimeout(() => {
            navigate('/success', {
              state: {
                formType: 'IT Support Request',
                reviewTeam: 'IT team',
                title: data.subject,
              },
            });
      }, 2000);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setSubmissionStatus('error');
      setSubmissionError(message);
    }
  };

  return (
    <>
      <SubmissionProgress
        status={submissionStatus}
        errorMessage={submissionError}
        onDismissError={() => setSubmissionStatus('idle')}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* ── Your Information ─────────────────────────────────── */}
        <div className="space-y-6">
          <div className="flex items-center justify-between border-b border-border pb-2">
            <h3 className="text-lg font-semibold text-gray-900">Your Information</h3>
            {authUser.isAuthenticated && (
              <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-sm">
                ✓ Signed in via OneLogin
              </span>
            )}
          </div>

          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <FormCombobox
                label="Requestor"
                options={emailOptions}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.email}
                required
                placeholder="Search by name or email..."
                helperText="Submitting for someone else? Search their name or pick their email from the dropdown."
                name="email"
              />
            )}
          />

          <FormSelect
            label="Department"
            options={deptOptions}
            placeholder="Select your department"
            {...register('department')}
            error={errors.department}
            required
          />
        </div>

        {/* ── Request Details ───────────────────────────────────── */}
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-gray-900 border-b border-border pb-2">
            Request Details
          </h3>

          <FormSelect
            label="Request Type"
            options={reqTypeOptions}
            placeholder="Select a request type"
            {...register('requestType')}
            error={errors.requestType}
            required
          />

          {isWebsiteIssue ? (
            <div className="space-y-4 pt-2">
              <p className="text-gray-600 text-sm text-center max-w-md mx-auto">
                No ticket needed — pick the site below. We&apos;ll open it with our
                annotation tool enabled so you can draw right on the page,
                describe what&apos;s wrong, and send it straight to the web team.
              </p>
              <WebsiteIssueList />
            </div>
          ) : (
            <>
              <FormInput
                label="Subject"
                placeholder="Brief summary of your request"
                {...register('subject')}
                error={errors.subject}
                required
              />

              <FormTextarea
                label="Description"
                placeholder="Describe your issue or request in detail..."
                rows={6}
                {...register('description')}
                error={errors.description}
                helperText="Be as detailed as possible so we can do our best to help."
                required
              />

              {captureSupported() && (
                <div className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50/60 p-4">
                  <p className="text-sm font-semibold text-gray-900">Show us the problem</p>
                  <p className="mt-0.5 mb-3 text-xs text-gray-500">
                    Capture any app window or your whole screen — your browser asks which one first,
                    so nothing is shared silently. Tip: pick just the app window.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => void captureShot()}
                      disabled={recElapsed !== null}
                      className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <Camera className="w-4 h-4" /> Capture a screenshot
                    </button>
                    <button
                      type="button"
                      onClick={() => void toggleRecording()}
                      className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold ${
                        recElapsed !== null
                          ? 'bg-red-600 text-white animate-pulse'
                          : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {recElapsed !== null ? (
                        <>
                          <Square className="w-3.5 h-3.5 fill-current" />
                          Stop recording · {Math.max(0, Math.ceil((RECORD_MAX_MS - recElapsed) / 1000))}s left
                        </>
                      ) : (
                        <>
                          <Video className="w-4 h-4" /> Record your screen
                          <span className="font-normal text-muted">· up to 60s</span>
                        </>
                      )}
                    </button>
                  </div>
                  {captureError && <p className="mt-2 text-xs font-medium text-red-600">{captureError}</p>}
                  {items.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {items.map((a) => (
                        <div key={a.key} className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm">
                          {a.type.startsWith('video/') ? (
                            <Video className="w-4 h-4 text-primary" />
                          ) : (
                            <ImageIcon className="w-4 h-4 text-primary" />
                          )}
                          <span className="truncate">{a.name}</span>
                          <span className="text-xs text-muted">{(a.size / 1e6).toFixed(1)} MB</span>
                          {a.uploading && (
                            <span className="flex items-center gap-1 text-xs text-gray-500">
                              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…
                            </span>
                          )}
                          {a.error && <span className="text-xs font-medium text-red-600">{a.error}</span>}
                          {a.url && !a.uploading && !a.error && (
                            <a href={a.url} target="_blank" rel="noreferrer" className="text-xs font-medium text-primary hover:underline">
                              Preview
                            </a>
                          )}
                          <button
                            type="button"
                            onClick={() => setItems((prev) => prev.filter((x) => x.key !== a.key))}
                            className="ml-auto text-muted hover:text-gray-600"
                            aria-label={`Remove ${a.name}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Actions ───────────────────────────────────────────── */}
        <div className="flex justify-end gap-4 pt-4 border-t border-gray-100">
          <Button type="button" variant="ghost" onClick={() => navigate('/portal')}>
            Cancel
          </Button>
          {!isWebsiteIssue && (
            <Button
              type="submit"
              loading={submissionStatus === 'submitting'}
              disabled={recElapsed !== null || uploadingCount > 0}
            >
              {uploadingCount > 0 ? 'Uploading capture…' : 'Submit Request'}
            </Button>
          )}
        </div>
      </form>

      {annotating && (
        <ScreenshotAnnotator
          imageDataUrl={annotating}
          onAttach={(jpeg, markCount) => {
            setAnnotating(null);
            void ingest(dataUrlToFile(jpeg, markCount > 0 ? 'annotated-screenshot.jpg' : 'screenshot.jpg'));
          }}
          onRetake={() => {
            setAnnotating(null);
            void captureShot();
          }}
          onCancel={() => setAnnotating(null)}
        />
      )}
    </>
  );
}
